import { useState } from 'react';
import { useReportMeta, downloadReport, useParticipations } from '../api';
import { useScope } from '../../store/scope';
import { PageLoader, ErrorState } from '../../components/ui';
import { useToast } from '../../components/Toast';
import { ApiRequestError } from '../../lib/api';
import type { BookingStatus } from '../../lib/types';

const REPORT_INFO: Record<string, { label: string; desc: string; usesStatus?: boolean }> = {
  'expo-summary': { label: 'Expo summary', desc: 'One-row-per-expo funnel: scans, visitors, views, bookings, revenue.' },
  stalls: { label: 'Stalls', desc: 'Per-stall performance — scans, bookings, booking value, conversion.' },
  racks: { label: 'Racks', desc: 'Per-rack performance with QR scan counts and revenue.' },
  products: { label: 'Products', desc: 'Every product with views, bookings, quantity and revenue.' },
  bookings: { label: 'Bookings', desc: 'One row per booking with visitor and totals.', usesStatus: true },
  'booking-items': { label: 'Booking line items', desc: 'One row per booked item — product, stall, rack, price.', usesStatus: true },
  visitors: { label: 'Visitors', desc: 'Visitor sessions with contact details and booking counts.' },
  'qr-scans': { label: 'QR scans', desc: 'Raw scan log — rack, device, time.' },
};

const STATUSES: BookingStatus[] = ['New', 'Contacted', 'Confirmed', 'Processing', 'Completed', 'Cancelled'];

export function ReportsPage() {
  const meta = useReportMeta();
  const { participationId } = useScope();
  const participations = useParticipations();
  const toast = useToast();

  const [expoId, setExpoId] = useState<string>('');
  const [format, setFormat] = useState('xlsx');
  const [status, setStatus] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [busy, setBusy] = useState('');

  // Default the expo filter to the current focus once, without fighting user edits.
  const effectiveExpo = expoId || participationId || '';

  const run = async (type: string) => {
    setBusy(type);
    try {
      await downloadReport(type, format, {
        participationId: effectiveExpo || undefined,
        status: REPORT_INFO[type]?.usesStatus && status ? status : undefined,
        from: from || undefined,
        to: to || undefined,
      });
      toast('Report downloaded', 'success');
    } catch (e) {
      toast(e instanceof ApiRequestError ? e.message : 'Report failed', 'error');
    } finally {
      setBusy('');
    }
  };

  if (meta.isLoading) return <PageLoader />;
  if (meta.error) return <ErrorState message={(meta.error as Error).message} onRetry={() => meta.refetch()} />;

  const types = meta.data?.types ?? [];
  const formats = meta.data?.formats ?? ['xlsx', 'csv', 'pdf'];
  const expoList = participations.data?.data ?? [];

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-2xl font-semibold text-ink-900">Reports</h1>
        <p className="text-sm text-ink-400">Pick filters, then download any report as a file.</p>
      </div>

      {/* Filter bar */}
      <div className="card grid gap-3 p-4 sm:grid-cols-2 lg:grid-cols-4">
        <label className="block">
          <span className="label">Expo</span>
          <select className="input" value={effectiveExpo} onChange={(e) => setExpoId(e.target.value)}>
            <option value="">All expos</option>
            {expoList.map((p) => (
              <option key={p._id} value={p._id}>
                {p.expoName}
              </option>
            ))}
          </select>
        </label>
        <label className="block">
          <span className="label">Format</span>
          <select className="input" value={format} onChange={(e) => setFormat(e.target.value)}>
            {formats.map((f) => (
              <option key={f} value={f}>
                {f.toUpperCase()}
              </option>
            ))}
          </select>
        </label>
        <label className="block">
          <span className="label">From</span>
          <input type="date" className="input" value={from} onChange={(e) => setFrom(e.target.value)} />
        </label>
        <label className="block">
          <span className="label">To</span>
          <input type="date" className="input" value={to} onChange={(e) => setTo(e.target.value)} />
        </label>
        <label className="block">
          <span className="label">Booking status (bookings reports only)</span>
          <select className="input" value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value="">Any</option>
            {STATUSES.map((s) => (
              <option key={s}>{s}</option>
            ))}
          </select>
        </label>
      </div>

      {/* Report cards */}
      <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
        {types.map((t) => {
          const info = REPORT_INFO[t] ?? { label: t, desc: '' };
          return (
            <div key={t} className="card flex flex-col p-4">
              <p className="font-medium text-ink-900">{info.label}</p>
              <p className="mt-1 flex-1 text-sm text-ink-400">{info.desc}</p>
              <button
                className="btn-outline mt-3 self-start"
                disabled={busy === t}
                onClick={() => run(t)}
              >
                {busy === t ? 'Preparing…' : `Download ${format.toUpperCase()}`}
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}
